import React, { useEffect, useState } from 'react'
import { IMaskInput } from 'react-imask'
import styles from './styles/Input.module.css'
import { IconPaths, IconSize, MaskPatterns, MaskType } from '../../../_metronic/helpers'
import { ButtonIcon } from '../Button/ButtonIcon'
import { getEntityMeta, useT } from '_metronic/i18n/Metronici18n'

interface InputProps {
  type?: 'text' | 'number' | 'password'
  id?: string
  name?: string
  placeholder?: string
  value?: string | number | undefined
  required?: boolean
  className?: string
  disabled?: boolean
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void
  isValid?: boolean
  isInvalid?: boolean
  mask?: MaskType
  errorMessage?: string
  entity?: string
  autoComplete?: 'off' | 'on' | 'new-password' | 'current-password'
  /** Granularidade do `type='number'` (ex.: `'any'` para aceitar decimais). */
  step?: string
  /** Faixa aceita pelo `type='number'`. */
  min?: number
  max?: number
  inputMode?: 'text' | 'numeric' | 'decimal'
  /** Id do texto de ajuda que descreve o campo. */
  'aria-describedby'?: string
  'aria-invalid'?: boolean
  /** Nome acessível quando o campo não tem <label> associado (ex.: linhas repetidas de opção). */
  'aria-label'?: string
}

export const Input: React.FC<InputProps> = ({
  type = 'text',
  id,
  name,
  placeholder,
  value: initialValue,
  required = false,
  className = '',
  disabled = false,
  onChange,
  onBlur,
  isValid,
  isInvalid,
  errorMessage,
  mask,
  entity,
  autoComplete,
  ...props
}) => {
  const { t } = useT()
  const meta = entity ? getEntityMeta(t, entity) : null

  const [value, setValue] = useState(initialValue)
  const [showPassword, setShowPassword] = useState(false)

  const resolvedPlaceholder =
    placeholder ??
    t(
      'placeholder.input',
      {
        name: meta?.labelLower,
        gender: meta?.gender,
      },
      'Preencha o campo...'
    )

  useEffect(() => setValue(initialValue), [initialValue])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value)
    if (onChange) onChange(e)
  }

  const inputType = type === 'password' ? (showPassword ? 'text' : 'password') : type
  // O estado visual de erro/sucesso vale também para o campo de senha (AC4): o
  // ícone de validação nativo é suprimido no SCSS (.form-control-solid) para não
  // colidir com o botão "mostrar senha".
  const validationClass = isInvalid ? 'is-invalid' : isValid ? 'is-valid' : ''

  const inputClasses = `
  form-control form-control-solid w-100
  ${validationClass}
  ${disabled ? styles.disabledInput : ''} 
  ${className}
`.trim()

  const getMask = () => {
    if (!mask) return undefined

    const pattern = MaskPatterns[mask]
    if (typeof pattern === 'string') return [{ mask: pattern, overwrite: true, lazy: true }]

    return pattern
  }

  const finalMask = getMask()

  // Associação programática label<->campo (AC5): emite `id` (fallback para `name`).
  const resolvedId = id ?? name
  // Erro ligado ao campo (WCAG 3.3.1/4.1.2): o leitor de tela lê a mensagem ao focar o campo.
  const mostraErro = !!(isInvalid && errorMessage)
  const errorId = mostraErro && resolvedId ? `${resolvedId}-erro` : undefined
  const ariaErro = {
    'aria-invalid': isInvalid || undefined,
    'aria-describedby': errorId,
  }

  return (
    <div className='w-100 position-relative'>
      {finalMask ? (
        <IMaskInput
          {...props}
          {...ariaErro}
          className={inputClasses}
          type={inputType}
          mask={finalMask}
          id={resolvedId}
          name={name}
          placeholder={resolvedPlaceholder}
          value={value}
          required={required}
          disabled={disabled}
          autoComplete={autoComplete}
          onChange={() => {}}
          onBlur={onBlur}
          onAccept={(val: string) => {
            setValue(val)
            if (onChange) {
              const fakeEvent = {
                target: { name, value: val },
              } as unknown as React.ChangeEvent<HTMLInputElement>
              onChange(fakeEvent)
            }
          }}
          dispatch={(appended, dynamicMasked) => {
            const number = (dynamicMasked.value + appended).replace(/\D/g, '')

            if (mask === MaskType.Phone) {
              return number.length > 10
                ? dynamicMasked.compiledMasks[1] // celular
                : dynamicMasked.compiledMasks[0] // fixo
            }

            if (mask === MaskType.CpfCns) {
              return number.length > 11
                ? dynamicMasked.compiledMasks[1] // cns
                : dynamicMasked.compiledMasks[0] // cpf
            }

            return dynamicMasked.compiledMasks[0]
          }}
        />
      ) : (
        <input
          {...props}
          {...ariaErro}
          className={inputClasses}
          type={inputType}
          id={resolvedId}
          name={name}
          placeholder={resolvedPlaceholder}
          value={value}
          required={required}
          disabled={disabled}
          autoComplete={autoComplete}
          onChange={handleChange}
          onBlur={onBlur}
        />
      )}
      {type === 'password' && (
        <ButtonIcon
          icon={showPassword ? IconPaths.EyeSlash : IconPaths.Eye}
          className='position-absolute top-50 end-0 translate-middle-y tap-target-44 d-flex align-items-center justify-content-center'
          onClick={() => setShowPassword(!showPassword)}
          iconSize={IconSize.Small}
          ariaLabel={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
        />
      )}
      {mostraErro && (
        <div className='fv-plugins-message-container'>
          <div className='fv-help-block'>
            <span id={errorId} role='alert'>
              {errorMessage}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
